import { useMemo } from "react";
import { Button, Card, Spin, Typography } from "antd";
import { useParams, useSearchParams } from "react-router-dom";
import { OVIZIA_APP_BASE_URL } from "constants";
import { useWorkspace } from "contexts/WorkspaceContext";
import { websiteOwnerTypeForWorkspace } from "./websiteUtils";
import "./OlreadiaFlowPages.css";

const { Paragraph, Title } = Typography;
const PENDING_WEBSITE_ACTION_KEY = "olreadia:pendingWebsiteAction";

export const buildOviziaHandoffUrl = ({
  ownerType,
  returnUrl,
  selectedWorkspaceId,
  targetPath = "/templates",
  templateId,
  websiteId,
  mode,
} = {}) => {
  const base = OVIZIA_APP_BASE_URL.replace(/\/+$/, "");
  const params = new URLSearchParams({
    source: "olreadia",
    flow: "website-create",
  });

  if (selectedWorkspaceId) params.set("workspaceId", selectedWorkspaceId);
  if (ownerType) params.set("ownerType", ownerType);
  if (returnUrl) params.set("returnUrl", returnUrl);
  if (targetPath) params.set("targetPath", targetPath);
  if (templateId) params.set("templateId", templateId);
  if (websiteId) params.set("websiteId", websiteId);
  if (mode) params.set("mode", mode);

  const sessionParams = new URLSearchParams();
  if (typeof window !== "undefined") {
    const sessionKeys = [
      "authToken",
      "refreshToken",
      "accessTokenExpiresAt",
      "refreshTokenExpiresAt",
      "userPrivileges",
    ];
    sessionKeys.forEach((key) => {
      const value = window.sessionStorage.getItem(key);
      if (value) sessionParams.set(key, value);
    });

    const clientId = window.localStorage.getItem("clientId");
    if (clientId) sessionParams.set("clientId", clientId);
  }

  const sessionFragment = sessionParams.toString();
  return `${base}/auth/handoff?${params.toString()}${sessionFragment ? `#${sessionFragment}` : ""}`;
};

export const buildOviziaWebsiteCreationUrl = (options = {}) =>
  buildOviziaHandoffUrl({ targetPath: "/templates", ...options });

const openOviziaTab = (url) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

const markPendingWebsiteAction = ({ action, ownerType, selectedWorkspaceId, websiteId }) => {
  localStorage.setItem(
    PENDING_WEBSITE_ACTION_KEY,
    JSON.stringify({
      action,
      workspaceId: selectedWorkspaceId,
      ownerType,
      websiteId: websiteId || "",
      lastWebsiteUpdatedAt: "",
      lastContentUpdatedAt: "",
      startedAt: Date.now(),
    }),
  );
};

export default function WebsiteCreationRedirect({ targetPanel = "" }) {
  const routeParams = useParams();
  const [searchParams] = useSearchParams();
  const {
    selectedWorkspaceId,
    selectedWorkspace,
    isOrganizationWorkspace,
    workspaceListReady,
  } = useWorkspace();

  const ownerType = websiteOwnerTypeForWorkspace({
    isOrganizationWorkspace,
    selectedWorkspace,
    searchOwnerType: searchParams.get("ownerType"),
  });

  const handoff = useMemo(() => {
    if (!selectedWorkspaceId && !workspaceListReady) return "";
    const websiteId = routeParams.websiteId || searchParams.get("websiteId") || undefined;
    const targetPath = targetPanel && websiteId
      ? `/editor/${encodeURIComponent(websiteId)}?panel=${encodeURIComponent(targetPanel)}`
      : undefined;
    const action = targetPanel === "domains"
      ? "domain"
      : targetPanel || websiteId
        ? targetPanel || "template"
        : "create";

    return {
      action,
      websiteId,
      url: buildOviziaWebsiteCreationUrl({
        ownerType,
        selectedWorkspaceId,
        targetPath,
        templateId: routeParams.templateId || searchParams.get("templateId") || undefined,
        websiteId,
        mode: searchParams.get("mode") || undefined,
      }),
    };
  }, [
    ownerType,
    routeParams.templateId,
    routeParams.websiteId,
    searchParams,
    selectedWorkspaceId,
    targetPanel,
    workspaceListReady,
  ]);

  return (
    <div className="olreadia-flow-page">
      <main className="olreadia-flow-main">
        <Card className="olreadia-flow-card">
          <Spin />
          <Title level={4} style={{ marginTop: 16 }}>
            Opening Ovizia
          </Title>
          <Paragraph>
            Continue in Ovizia to manage the website for this workspace.
          </Paragraph>
          {handoff?.url ? (
            <Button
              type="primary"
              onClick={() => {
                markPendingWebsiteAction({
                  action: handoff.action,
                  ownerType,
                  selectedWorkspaceId,
                  websiteId: handoff.websiteId,
                });
                openOviziaTab(handoff.url);
              }}
            >
              Continue in Ovizia
            </Button>
          ) : null}
        </Card>
      </main>
    </div>
  );
}
